// Duplicating a design template. A template owns a screen's whole look, so
// "this screen needs a different look" means a template of its own —
// copying an existing one is the short path to that.

import { toast } from "@pej/kore/components";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@/api/error";
import { type TemplateBody, useCreateTemplate, useTemplates } from "@/api/templates";
import type { Template } from "@/api/types";
import { toastMutationError } from "@/components/error-toast";

/** Every design field of a template as a create/patch body — null means
 *  "unset", which the wire expresses by omission. */
export function toTemplateBody(template: Template): TemplateBody {
    return {
        backgroundColor: template.backgroundColor,
        backgroundImageAssetId: template.backgroundImageAssetId ?? undefined,
        logoAssetId: template.logoAssetId ?? undefined,
        logoPosition: template.logoPosition,
        fontStack: template.fontStack ?? undefined,
        fontAssetId: template.fontAssetId ?? undefined,
        colorSlots: template.colorSlots,
        customCss: template.customCss ?? undefined,
        // The resolved URLs travel with the ids they belong to. They are pinned
        // to an asset generation, not to a template, so they are just as valid
        // for the copy — and leaving them behind would hand the duplicate an
        // asset reference no screen can render, since the display endpoint
        // cannot resolve one for itself.
        backgroundImageUrl: template.backgroundImageUrl ?? undefined,
        logoUrl: template.logoUrl ?? undefined,
        fontFaceUrl: template.fontFaceUrl ?? undefined,
    };
}

/** Template names are unique per workspace, so a duplicate picks the first
 *  free "(copy)" suffix rather than letting the server 409 the obvious one. */
export function duplicateName(source: string, taken: Iterable<string>): string {
    const names = new Set(taken);
    const first = `${source} (copy)`;
    if (!names.has(first)) return first;
    for (let n = 2; n < 100; n++) {
        const candidate = `${source} (copy ${n})`;
        if (!names.has(candidate)) return candidate;
    }
    return first; // 99 copies deep: let the server's uniqueness check answer.
}

/** Duplicate a saved template and open the copy — from the list's rows and
 *  the template page's header alike. */
export function useDuplicateTemplate(workspaceId: string) {
    const navigate = useNavigate();
    const templatesQuery = useTemplates(workspaceId);
    const createTemplate = useCreateTemplate(workspaceId);

    async function duplicate(template: Template) {
        const taken = (templatesQuery.data ?? []).map((t) => t.name);
        try {
            const created = await createTemplate.mutateAsync({
                ...toTemplateBody(template),
                name: duplicateName(template.name, taken),
            });
            toast.success(`Duplicated as ${created.name}`);
            void navigate({
                to: "/w/$workspaceId/templates/$templateId",
                params: { workspaceId, templateId: created.id },
            });
        } catch (err) {
            if (err instanceof ApiError) {
                // A name taken since load is Conflict.AlreadyExists, which
                // friendlyMessage says from its entity and field params.
                toastMutationError("Duplicate failed", err);
                return;
            }
            throw err;
        }
    }

    return { duplicate, isPending: createTemplate.isPending };
}
